import { DateTime } from 'luxon'
import i18n from 'i18n'

export const DASHBOARD_DATE_RANGE_STORAGE_KEY = 'dashboardAdvancedFilters'
export const DASHBOARD_DATE_RANGE_STORAGE_VERSION = 4

const getDashboardStorageDate = () => DateTime.now()
  .setZone('America/Mexico_City')
  .toFormat('yyyy-MM-dd')

// `labelKey` values are i18n keys resolved with i18n.t at display time (see
// DateRangeFilterModal and getPresetLabel), so the dropdowns and summaries
// follow the active language.
export const DATE_FILTER_TYPES = [
  { value: 'custom', labelKey: 'dateFilter.types.custom' },
  { value: 'day', labelKey: 'dateFilter.types.day' },
  { value: 'week', labelKey: 'dateFilter.types.week' },
  { value: 'month', labelKey: 'dateFilter.types.month' },
  { value: 'year', labelKey: 'dateFilter.types.year' }
]

export const DATE_PRESETS_BY_TYPE = {
  custom: [{ value: 'custom', labelKey: 'dateFilter.presets.custom' }],
  day: [
    { value: 'today', labelKey: 'dateFilter.presets.today' },
    { value: 'yesterday', labelKey: 'dateFilter.presets.yesterday' },
    { value: 'last_workday', labelKey: 'dateFilter.presets.last_workday', backendResolved: true },
    { value: 'previous_workday', labelKey: 'dateFilter.presets.previous_workday', backendResolved: true },
    { value: 'last_harvest_day', labelKey: 'dateFilter.presets.last_harvest_day', backendResolved: true },
    { value: 'previous_harvest_day', labelKey: 'dateFilter.presets.previous_harvest_day', backendResolved: true },
    { value: 'last_agro_day', labelKey: 'dateFilter.presets.last_agro_day', backendResolved: true },
    { value: 'previous_agro_day', labelKey: 'dateFilter.presets.previous_agro_day', backendResolved: true }
  ],
  week: [
    { value: 'last_7_days', labelKey: 'dateFilter.presets.last_7_days' },
    { value: 'week_number', labelKey: 'dateFilter.presets.week_number' },
    { value: 'current_week', labelKey: 'dateFilter.presets.current_week' },
    { value: 'previous_week', labelKey: 'dateFilter.presets.previous_week' }
  ],
  month: [
    { value: 'current_month', labelKey: 'dateFilter.presets.current_month' },
    { value: 'previous_month', labelKey: 'dateFilter.presets.previous_month' },
    { value: 'last_30_days', labelKey: 'dateFilter.presets.last_30_days' }
  ],
  year: [
    { value: 'current_season', labelKey: 'dateFilter.presets.current_season', backendResolved: true },
    { value: 'previous_season', labelKey: 'dateFilter.presets.previous_season', backendResolved: true },
    { value: 'last_12_months', labelKey: 'dateFilter.presets.last_12_months' }
  ]
}

export const createDefaultDateRange = (date) => ({
  dateFrom: date,
  dateTo: date,
  dateFilterType: 'custom',
  datePreset: 'custom',
  weekNumber: String(DateTime.fromISO(date).weekNumber),
  weekYear: String(DateTime.fromISO(date).weekYear),
  sectorId: '',
  groupId: '',
  activityId: '',
  blockGroupName: '',
  deviceGroupName: '',
  activityName: ''
})

const toIsoStartDateTime = (date) => {
  if (!date) return ''
  return new Date(date).toISOString()
}

const toIsoEndDateTime = (date) => {
  if (!date) return ''
  return new Date(`${date}T23:59:59.999Z`).toISOString()
}

const toDateString = dateTime => dateTime.toFormat('yyyy-MM-dd')

const mergeRange = (range, dateFrom, dateTo) => ({
  ...range,
  dateFrom,
  dateTo
})

const backendResolvedPresets = new Set(
  Object.values(DATE_PRESETS_BY_TYPE)
    .flat()
    .filter(preset => preset.backendResolved)
    .map(preset => preset.value)
)

export const resolveDashboardDateRangeFilter = (range, today = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd')) => {
  const now = DateTime.fromISO(today).setZone('America/Mexico_City')
  const dateFilterType = range.dateFilterType || 'custom'
  const datePreset = range.datePreset || 'custom'

  if (dateFilterType === 'custom') return range
  if (backendResolvedPresets.has(datePreset)) return { ...range, dateFrom: '', dateTo: '' }

  if (dateFilterType === 'day') {
    if (datePreset === 'yesterday') {
      const yesterday = toDateString(now.minus({ days: 1 }))
      return mergeRange(range, yesterday, yesterday)
    }

    return mergeRange(range, toDateString(now), toDateString(now))
  }

  if (dateFilterType === 'week') {
    if (datePreset === 'last_7_days') {
      return mergeRange(range, toDateString(now.minus({ days: 6 })), toDateString(now))
    }

    if (datePreset === 'previous_week') {
      const previousWeek = now.minus({ weeks: 1 })
      return mergeRange(range, toDateString(previousWeek.startOf('week')), toDateString(previousWeek.endOf('week')))
    }

    if (datePreset === 'week_number') {
      const weekYear = Number(range.weekYear || now.weekYear)
      const weekNumber = Number(range.weekNumber || now.weekNumber)
      const firstWeekDay = DateTime.fromObject({ weekYear, weekNumber, weekday: 1 })
      return mergeRange(range, toDateString(firstWeekDay), toDateString(firstWeekDay.plus({ days: 6 })))
    }

    return mergeRange(range, toDateString(now.startOf('week')), toDateString(now.endOf('week')))
  }

  if (dateFilterType === 'month') {
    if (datePreset === 'previous_month') {
      const previousMonth = now.minus({ months: 1 })
      return mergeRange(range, toDateString(previousMonth.startOf('month')), toDateString(previousMonth.endOf('month')))
    }

    if (datePreset === 'last_30_days') {
      return mergeRange(range, toDateString(now.minus({ days: 29 })), toDateString(now))
    }

    return mergeRange(range, toDateString(now.startOf('month')), toDateString(now.endOf('month')))
  }

  if (dateFilterType === 'year' && datePreset === 'last_12_months') {
    return mergeRange(range, toDateString(now.minus({ months: 12 }).plus({ days: 1 })), toDateString(now))
  }

  return range
}

export const buildDashboardDateRangeQuery = (range) => {
  const resolvedRange = resolveDashboardDateRangeFilter(range)
  const params = new URLSearchParams()
  const dateFromIso = toIsoStartDateTime(resolvedRange.dateFrom)
  const dateToIso = toIsoEndDateTime(resolvedRange.dateTo)

  if (dateFromIso) params.set('dateFrom', dateFromIso)

  if (dateToIso) params.set('dateTo', dateToIso)
  if (resolvedRange.dateFilterType && resolvedRange.dateFilterType !== 'custom') params.set('dateFilterType', resolvedRange.dateFilterType)
  if (resolvedRange.datePreset && resolvedRange.datePreset !== 'custom') params.set('datePreset', resolvedRange.datePreset)
  if (resolvedRange.weekNumber && resolvedRange.datePreset === 'week_number') params.set('weekNumber', resolvedRange.weekNumber)
  if (resolvedRange.weekYear && resolvedRange.datePreset === 'week_number') params.set('weekYear', resolvedRange.weekYear)
  if (resolvedRange.sectorId) params.set('blockGroupId', resolvedRange.sectorId)
  if (resolvedRange.groupId) params.set('deviceGroupId', resolvedRange.groupId)
  if (resolvedRange.activityId) params.set('activityId', resolvedRange.activityId)

  return params.toString()
}

const getPresetLabel = (dateFilterType, datePreset) => {
  const preset = (DATE_PRESETS_BY_TYPE[dateFilterType] || [])
    .find(option => option.value === datePreset)
  return preset?.labelKey ? i18n.t(preset.labelKey) : ''
}

const getFilterSummaryLabel = (range, idKey, nameKey, fallbackLabelKey) => {
  if (!range[idKey]) return ''
  return range[nameKey] || i18n.t(fallbackLabelKey)
}

export const getDateRangeSummary = (range, today) => {
  const resolvedRange = resolveDashboardDateRangeFilter(range, today)
  const presetLabel = getPresetLabel(range.dateFilterType, range.datePreset)
  const dateSummary = resolvedRange.dateFrom && resolvedRange.dateTo
    ? resolvedRange.dateFrom === resolvedRange.dateTo ? resolvedRange.dateFrom : `${resolvedRange.dateFrom} a ${resolvedRange.dateTo}`
    : ''

  if (!dateSummary && presetLabel) {
    const summary = [presetLabel]
    const filterLabels = [
      getFilterSummaryLabel(range, 'sectorId', 'blockGroupName', 'dateFilter.group'),
      getFilterSummaryLabel(range, 'groupId', 'deviceGroupName', 'dateFilter.deviceGroup'),
      getFilterSummaryLabel(range, 'activityId', 'activityName', 'dateFilter.activity')
    ].filter(Boolean)
    summary.push(...filterLabels)
    return summary.join(' · ')
  }

  if (!dateSummary) return ''

  const summary = [presetLabel && range.dateFilterType !== 'custom' ? `${presetLabel} (${dateSummary})` : dateSummary]

  const filterLabels = [
    getFilterSummaryLabel(range, 'sectorId', 'blockGroupName', 'dateFilter.group'),
    getFilterSummaryLabel(range, 'groupId', 'deviceGroupName', 'dateFilter.deviceGroup'),
    getFilterSummaryLabel(range, 'activityId', 'activityName', 'dateFilter.activity')
  ].filter(Boolean)
  summary.push(...filterLabels)

  return summary.join(' · ')
}

const isValidStoredRange = (value) => (
  value &&
  typeof value === 'object' &&
  typeof value.dateFrom === 'string' &&
  typeof value.dateTo === 'string'
)

export const readStoredDashboardDateRange = (defaultDate, storage = localStorage) => {
  try {
    const storedValue = storage?.getItem(DASHBOARD_DATE_RANGE_STORAGE_KEY)
    if (!storedValue) return createDefaultDateRange(defaultDate)

    const parsedValue = JSON.parse(storedValue)
    if (!isValidStoredRange(parsedValue)) return createDefaultDateRange(defaultDate)
    if (parsedValue.storageVersion !== DASHBOARD_DATE_RANGE_STORAGE_VERSION) return createDefaultDateRange(defaultDate)
    if (parsedValue.storedAtDate !== defaultDate) {
      storage?.removeItem?.(DASHBOARD_DATE_RANGE_STORAGE_KEY)
      return createDefaultDateRange(defaultDate)
    }

    const { storageVersion, storedAtDate, ...storedRange } = parsedValue

    return {
      ...createDefaultDateRange(defaultDate),
      ...storedRange,
      sectorId: parsedValue.sectorId || '',
      groupId: parsedValue.groupId || '',
      activityId: parsedValue.activityId || '',
      blockGroupName: parsedValue.blockGroupName || '',
      deviceGroupName: parsedValue.deviceGroupName || '',
      activityName: parsedValue.activityName || ''
    }
  } catch (error) {
    return createDefaultDateRange(defaultDate)
  }
}

export const writeStoredDashboardDateRange = (range, storage = localStorage, storedAtDate = getDashboardStorageDate()) => {
  try {
    storage?.setItem(DASHBOARD_DATE_RANGE_STORAGE_KEY, JSON.stringify({
      ...range,
      storageVersion: DASHBOARD_DATE_RANGE_STORAGE_VERSION,
      storedAtDate
    }))
  } catch (error) {
    // Ignore storage failures so dashboard filters still work in private or restricted contexts.
  }
}
